/**
 * EditorDataCompra — a caixinha que corrige a DATA da compra de um produto "em aberto" (DA-368).
 *
 * O carrinho da cotacao carimba `ultima_compra_em = HOJE` num clique (o fluxo normal e registrar no
 * mesmo dia). Esta peca cobre os dois casos em que "hoje" nao serve: o mutirao de compras retroativas
 * e a correcao pontual. A data fica editavel onde o selo aparece — na linha da cotacao (as duas
 * vistas) e no selo da lista de Produtos. Mesmo gesto da caixinha de preco (EditorPrecoCatalogo):
 * popover `position: fixed` ancorado no selo (a grade rola dentro de um container, entao nada de
 * `absolute` — regra do CLAUDE.md), Enter grava, Esc / clique-fora cancela.
 *
 * A gravacao e por PRODUTO (`PATCH /api/produtos/:id/ultima-compra`), nao pela cotacao — a marca de
 * compra em aberto vive no produto e atravessa cotacoes (DA-368). Muda so a data; a marca e o
 * fornecedor seguem.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { formatDataSP } from '@fiodecobre/shared';

const LARGURA = 210;
/** Altura estimada — so decide se abre pra cima ou pra baixo. */
const ALTURA = 150;

/**
 * ISO/timestamptz -> "AAAA-MM-DD" no fuso de SP, reusando o helper vetado (DA-330) em vez de remontar
 * a data na mao — `formatDataSP` ja resolve as duas grafias que a API devolve, e o input `type=date`
 * quer o formato ISO curto.
 */
function isoParaInputDate(iso: string | null): string {
  if (!iso) return '';
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(formatDataSP(iso));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

export interface AlvoEdicaoData {
  produtoId: number;
  /** item da prateleira Novos Produtos (cotacao): a data mora la, nao no produto */
  novoProdutoId?: number | null;
  /** retangulo do selo clicado — a caixa nasce colada nele */
  rect: { top: number; left: number; bottom: number; right: number; width: number };
  /** ISO da `ultima_compra_em` atual (preenche o campo) */
  dataAtualISO: string | null;
  /** nome do fornecedor da ultima compra — so exibicao */
  fornNome: string | null;
}

export function EditorDataCompra(props: {
  alvo: AlvoEdicaoData;
  salvando: boolean;
  onCancelar: () => void;
  onConfirmar: (dataYYYYMMDD: string) => void;
}) {
  const { alvo, salvando, onCancelar, onConfirmar } = props;

  const [data, setData] = useState(() => isoParaInputDate(alvo.dataAtualISO));
  const caixaRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Foco sincrono (layout effect, nao setTimeout) — mesma regra do ComboBox/EditorPrecoCatalogo.
  useLayoutEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clicar fora fecha sem gravar (popover de celula, nao modal — igual a caixinha de preco).
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) onCancelar();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onCancelar]);

  const podeSalvar = /^\d{4}-\d{2}-\d{2}$/.test(data) && !salvando;

  function confirmar() {
    if (!podeSalvar) return;
    onConfirmar(data);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancelar(); return; }
    // Enter grava. Sem o preventDefault o `enter-as-tab` global levaria o foco adiante e o operador
    // sairia da caixa sem gravar.
    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); confirmar(); }
  }

  // Cabe embaixo? Senao vira pra cima. E nunca vaza pela direita da janela.
  const abreParaCima = alvo.rect.bottom + ALTURA > window.innerHeight;
  const top = abreParaCima ? Math.max(8, alvo.rect.top - ALTURA - 4) : alvo.rect.bottom + 4;
  const left = Math.max(8, Math.min(alvo.rect.left, window.innerWidth - LARGURA - 8));

  return (
    <div
      ref={caixaRef}
      onKeyDown={onKeyDown}
      // Acima da vista lado a lado (painel `fixed`) e abaixo do PainelEscolha (modal 1200) — mesmo
      // andar da caixinha de preco.
      style={{
        position: 'fixed', top, left, width: LARGURA, zIndex: 1150,
        background: 'var(--panel-bg)', border: '1px solid var(--primary)', borderRadius: 8,
        boxShadow: '0 12px 32px rgba(0,0,0,0.22)', padding: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 5 }}>
          🛒 Data da compra
        </div>
        {alvo.fornNome && (
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {alvo.fornNome}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="date"
        value={data}
        onChange={(e) => setData(e.target.value)}
        style={{ width: '100%', padding: '5px 8px', borderRadius: 4, fontSize: 13, fontWeight: 700 }}
      />

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancelar}
          disabled={salvando}
          style={{
            padding: '5px 12px', borderRadius: 4, border: '1px solid var(--border)',
            background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={confirmar}
          disabled={!podeSalvar}
          className="btn-primario"
          style={{ padding: '5px 14px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: podeSalvar ? 1 : 0.5 }}
        >
          {salvando && <Loader2 size={12} className="animate-spin" />}
          Salvar
        </button>
      </div>
    </div>
  );
}
